import React, { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useApp } from '../context/AppContext';
import { LOGO_DARK_URL } from '../mock';
import { ArrowLeft, Printer, Calendar, MapPin, Building2 } from 'lucide-react';

export default function PullSheet() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { projects, items, kits, expandProjectGear, categories: CATEGORIES, categoryColor } = useApp();

  const project = projects.find((p) => p.id === id);

  const grouped = useMemo(() => {
    if (!project) return [];
    const expanded = expandProjectGear(project);
    const byCat = new Map();
    expanded.forEach((e) => {
      const it = items.find((i) => i.code === e.itemCode);
      if (!it) return;
      if (!byCat.has(it.category)) byCat.set(it.category, []);
      byCat.get(it.category).push({ item: it, qty: e.qty, sources: e.sources });
    });
    // Preserve category order
    const ordered = [];
    CATEGORIES.forEach((c) => {
      if (byCat.has(c.name))
        ordered.push({ category: c.name, rows: byCat.get(c.name).sort((a, b) => a.item.name.localeCompare(b.item.name)) });
    });
    return ordered;
  }, [project, items, expandProjectGear]);

  if (!project) {
    return (
      <div className="min-h-screen bg-white text-neutral-900 p-8">
        <p>Project not found.</p>
      </div>
    );
  }

  const totalUnits = grouped.reduce((s, g) => s + g.rows.reduce((ss, r) => ss + r.qty, 0), 0);
  const totalItems = grouped.reduce((s, g) => s + g.rows.length, 0);
  const today = new Date().toLocaleDateString();

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .pull-sheet { padding: 0 !important; }
          body { background: white !important; }
          /* Print-only Scan column with QR codes */
          .pull-sheet .scan-col { display: table-cell !important; }
          /* Slight vertical spacing so phones don't grab QRs from adjacent rows */
          .pull-sheet tbody td { padding-top: 5px !important; padding-bottom: 5px !important; vertical-align: middle; }
          .pull-sheet tr { page-break-inside: avoid; }
        }
        .pull-sheet { background: white; color: #111; }
        .pull-sheet table { width: 100%; border-collapse: collapse; }
        .pull-sheet th, .pull-sheet td { text-align: left; padding: 6px 8px; border-bottom: 1px solid #e5e5e5; font-size: 12px; }
        .pull-sheet th { font-size: 10px; text-transform: uppercase; letter-spacing: 0.06em; color: #666; font-weight: 600; border-bottom: 1px solid #999; }
        .pull-sheet .cat-header { background: #f5f5f5; padding: 6px 8px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; border-top: 2px solid #111; border-bottom: 1px solid #999; }
        .pull-sheet .checkbox { width: 14px; height: 14px; border: 1.5px solid #111; display: inline-block; vertical-align: middle; }
        /* Hide the Scan column on-screen — QR codes only appear on print */
        .pull-sheet .scan-col { display: none; }
      `}</style>

      {/* Print toolbar */}
      <div className="no-print sticky top-0 z-10 bg-neutral-950 border-b border-neutral-900 px-5 py-3 flex items-center gap-3">
        <button
          onClick={() => navigate(`/projects/${project.id}`)}
          className="text-xs text-neutral-300 hover:text-white inline-flex items-center gap-1"
        >
          <ArrowLeft size={13} /> Back to project
        </button>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => window.print()}
            className="btn-amber rounded-md px-3 py-1.5 text-xs flex items-center gap-1.5"
          >
            <Printer size={13} /> Print
          </button>
        </div>
      </div>

      {/* Sheet */}
      <div className="pull-sheet min-h-screen px-10 py-8 max-w-4xl mx-auto">
        <div className="flex items-start justify-between border-b-2 border-neutral-900 pb-4 mb-6">
          <div>
            <img src={LOGO_DARK_URL} alt="Whiplash Film & Creative" style={{ height: 34, display: 'block', marginBottom: 8 }} />
            <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-600">Gear Pull Sheet</div>
          </div>
          <div className="text-right text-[11px] text-neutral-600">
            <div>Generated {today}</div>
            <div>By Whiplash Film & Creative</div>
          </div>
        </div>

        <h1 className="text-2xl font-bold mb-3" style={{ color: '#111' }}>
          {project.name}
        </h1>
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-[12px] text-neutral-700 mb-2">
          {project.client && (
            <span className="inline-flex items-center gap-1.5">
              <Building2 size={11} /> {project.client}
            </span>
          )}
          {project.location && (
            <span className="inline-flex items-center gap-1.5">
              <MapPin size={11} /> {project.location}
            </span>
          )}
          {project.shootDates?.length > 0 && (
            <span className="inline-flex items-center gap-1.5">
              <Calendar size={11} /> {project.shootDates.join(' → ')}
            </span>
          )}
        </div>
        {project.notes && (
          <p className="text-[11px] text-neutral-600 leading-relaxed mb-3">{project.notes}</p>
        )}

        {/* Summary */}
        <div className="flex gap-6 text-[11px] text-neutral-700 mb-5 pt-3 border-t border-neutral-300">
          <span>
            <strong>{totalItems}</strong> line items
          </span>
          <span>
            <strong>{totalUnits}</strong> total units
          </span>
          <span>
            <strong>{project.kitCodes?.length || 0}</strong> kit{(project.kitCodes?.length || 0) === 1 ? '' : 's'}
          </span>
        </div>

        {/* Kits used (header reference) */}
        {project.kitCodes?.length > 0 && (
          <div className="mb-5 text-[11px] text-neutral-700">
            <div className="text-[10px] tracking-wider uppercase text-neutral-500 mb-1">Kits included</div>
            <ul className="list-disc list-inside">
              {project.kitCodes.map((kc) => {
                const kit = kits.find((k) => k.code === kc);
                return (
                  <li key={kc}>
                    <span className="font-mono">{kc}</span> — {kit?.name || ''} ({kit?.itemCodes?.length || 0} items)
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Items grouped by category */}
        {grouped.length === 0 ? (
          <p className="text-neutral-500 text-sm">No gear on this project.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th style={{ width: 30 }}>✓</th>
                <th style={{ width: 90 }}>Code</th>
                <th>Item</th>
                <th style={{ width: 60, textAlign: 'right' }}>Qty</th>
                <th style={{ width: 110 }}>Source</th>
                <th className="scan-col" style={{ width: '1in', textAlign: 'center' }}>Scan</th>
              </tr>
            </thead>
            <tbody>
              {grouped.map((g) => (
                <React.Fragment key={g.category}>
                  <tr>
                    <td colSpan={6} className="cat-header">
                      <span
                        className="cat-dot"
                        style={{ background: categoryColor(g.category), marginRight: 6 }}
                      />
                      {g.category} · {g.rows.length} item{g.rows.length === 1 ? '' : 's'}
                    </td>
                  </tr>
                  {g.rows.map(({ item, qty, sources }) => {
                    const fromKit = sources.find((s) => s.startsWith('kit:'));
                    const direct = sources.includes('direct');
                    let src = '';
                    if (fromKit && direct) src = `Kit + Direct`;
                    else if (fromKit) src = fromKit.replace('kit:', 'Kit ');
                    else src = 'Direct';
                    return (
                      <tr key={item.code}>
                        <td>
                          <span className="checkbox" />
                        </td>
                        <td style={{ fontFamily: 'monospace' }}>{item.code}</td>
                        <td>{item.name}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{qty}</td>
                        <td style={{ fontSize: 10, color: '#666' }}>{src}</td>
                        <td className="scan-col" style={{ textAlign: 'center' }}>
                          {/*
                            0.765in QR (still >= 0.75in requirement) with a
                            4-module quiet zone. Tighter row padding keeps
                            adjacent QRs well separated visually.
                          */}
                          <div style={{ width: '0.765in', height: '0.765in', display: 'inline-block' }}>
                            <QRCodeSVG
                              value={item.qrCode || item.code}
                              size={73}
                              level="M"
                              includeMargin={true}
                              style={{ width: '100%', height: '100%' }}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}

        <div className="mt-8 pt-4 border-t border-neutral-300 grid grid-cols-2 gap-6 text-[11px]">
          <div>
            <div className="text-[10px] tracking-wider uppercase text-neutral-500 mb-2">
              Packed by
            </div>
            <div className="border-b border-neutral-400 h-6" />
            <div className="text-neutral-500 mt-1">Name & date</div>
          </div>
          <div>
            <div className="text-[10px] tracking-wider uppercase text-neutral-500 mb-2">
              Returned by
            </div>
            <div className="border-b border-neutral-400 h-6" />
            <div className="text-neutral-500 mt-1">Name & date</div>
          </div>
        </div>
      </div>
    </>
  );
}
